import { useParams, generatePath, useLocation } from 'react-router'
import { Paths, RoomEventUidQueryKey } from 'src/constants/paths'
import { useFeatureFlags } from 'src/features/trial/api/getFeatureFlags'

import { useDocRevisionForBrowse } from '../../api'
import { useCreateSignAuthentication } from '../../api/createSignAuthentication'
import { ExplanationRoomEventProvider } from '../../context/ExplanationRoomEvent'
import { useThisSession } from '../../hooks/useThisSession'
import { isAgreementCompletedOrRejected } from '../../utils/explanationRevisionStatusUtils'
import { DocuSignIframeContent } from '../DocuSignIframeContent/DocuSignIframeContent'
import { SignAuthChecker } from '../SignAuthChecker/SignAuthChecker'
import { SPExplanationRoomLayout } from '../SPExplanationRoomLayout/SPExplanationRoomLayout'
import { SPExplanationVideoCall } from '../SPExplanationVideoCall/SPExplanationVideoCall'

type Param = {
  trialUid: string
  sessionUid: string
  docRevisionUid: string
  signerActorUid: string
}

export const SPPatientDocumentSignScreen: React.FC = () => {
  const {
    trialUid = '',
    sessionUid = '',
    docRevisionUid = '',
    signerActorUid = '',
  } = useParams<Param>()

  const { search } = useLocation()

  const searchParams = new URLSearchParams(search)

  // TODO: ファイルURL等は不要なので別のqueryを作る
  const { data: docRevision } = useDocRevisionForBrowse({
    explanationDocRevisionUid: docRevisionUid,
  })
  const { data: signAuth, isValidating } = useCreateSignAuthentication({
    explanationSessionUid: sessionUid,
    explanationDocRevisionUid: docRevisionUid,
    signerActorUid,
    revalidateOnFocus: false,
  })

  const { data: featureFlags } = useFeatureFlags({
    trialUid: !!trialUid ? trialUid : null,
  })

  const thisSession = useThisSession({ sessionUid, forPatient: true })

  if (
    !docRevision ||
    !thisSession.fetched ||
    !signAuth ||
    !featureFlags ||
    isValidating
  ) {
    return null
  }

  const roomTopPath = generatePath(Paths.PatientExplanationRoomTop, {
    trialUid,
    sessionUid,
  })

  return (
    <ExplanationRoomEventProvider
      roomType="Session"
      isSP
      deviceActorUid={thisSession.patient.uid}
      trialUid={trialUid}
      explanationRevisionUid={thisSession.explanationRevisionUid}
      sessionUid={sessionUid}
      explanationPatientUid={thisSession.patient.uid}
      patientPhoneNumber={thisSession.patient.mobileNumber ?? undefined}
      deviceActorType="Patient"
      deviceActorTrialHospitalUid={thisSession.trialHospital.uid}
    >
      <SPExplanationRoomLayout
        headerTitle={docRevision.icfDocumentRevision.name}
        trialUid={trialUid}
        sessionUid={sessionUid}
        isAgreementCompletedOrRejected={isAgreementCompletedOrRejected(
          thisSession.revisionStatus,
        )}
        showLeaveButton={false}
      >
        <SPExplanationVideoCall
          roomUid={thisSession.videoCallRoomUid}
          candidateId={thisSession.patient.candidateId}
          canShrinkVideoCall
          trialPatientUID={thisSession.patient.trialPatientUid}
        />
        <SignAuthChecker
          isSP={true}
          trialUid={trialUid}
          explanationSignAuthUid={signAuth.signAuthenticationUid}
          roomTopPath={roomTopPath}
        >
          <DocuSignIframeContent
            isSP
            trialUid={trialUid}
            signAuthUid={signAuth.signAuthenticationUid}
            roomTopPath={roomTopPath}
            sessionUid={sessionUid}
            docRevisionUid={docRevisionUid}
            signerDisplayName={`候補ID: ${thisSession.patient.candidateId}`}
            signerActorUid={signerActorUid}
            completePath={
              generatePath(Paths.PatientExplanationRoomSignCompleted, {
                trialUid,
                sessionUid,
              }) +
              `?${RoomEventUidQueryKey}=${searchParams.get(RoomEventUidQueryKey)}`
            }
            roomEventUid={searchParams.get(RoomEventUidQueryKey) ?? ''}
          />
        </SignAuthChecker>
      </SPExplanationRoomLayout>
    </ExplanationRoomEventProvider>
  )
}
